import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAccounts, getCampaigns } from '../../services/admin/campaignStatistics';
import { adminTrafficQualityService } from '../../services/admin/trafficQuality';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui';
import { cn } from '../../lib/utils';
import TrafficQualityPage from '../campaigns/TrafficQualityPage';
import type { TrafficQualityPeriod, AccountTrafficQuality } from '../../types/trafficQuality';

const PERIODS: { key: string; value: TrafficQualityPeriod }[] = [
  { key: 'today', value: 'today' },
  { key: '7d', value: 'last_7_days' },
  { key: '30d', value: 'last_30_days' },
  { key: '90d', value: 'last_90_days' },
];

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}
function ratioColor(ratio: number): string {
  if (ratio >= 70) return 'text-red-600 font-semibold';
  if (ratio >= 40) return 'text-yellow-600 font-medium';
  return 'text-gray-600';
}

export default function AdminTrafficQualityPage() {
  const { t } = useTranslation('trafficQuality');
  const [accountId, setAccountId] = useState<number | null>(null);
  const [campaignSel, setCampaignSel] = useState<number | 'overall'>('overall');
  const [period, setPeriod] = useState<TrafficQualityPeriod>('last_7_days');
  const [search, setSearch] = useState('');

  const accountsQ = useQuery({
    queryKey: ['admin-tq-accounts', search],
    queryFn: () => getAccounts(search || undefined),
  });
  const campaignsQ = useQuery({
    queryKey: ['admin-tq-campaigns', accountId],
    queryFn: () => getCampaigns(accountId as number),
    enabled: !!accountId,
  });

  const overallQ = useQuery({
    queryKey: ['admin-tq-account', accountId, period],
    queryFn: () => adminTrafficQualityService.getAccountSummary(accountId as number, period),
    enabled: !!accountId && campaignSel === 'overall',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin_title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('admin_subtitle')}</p>
      </div>

      {/* Selectors */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('admin.client_label')}</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.client_search')}
              className="w-full mb-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={accountId ?? ''}
              onChange={(e) => {
                setAccountId(e.target.value ? parseInt(e.target.value, 10) : null);
                setCampaignSel('overall');
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('admin.client_placeholder')}</option>
              {(accountsQ.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.display_name}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('admin.campaign_label')}</label>
            <select
              value={String(campaignSel)}
              disabled={!accountId}
              onChange={(e) => setCampaignSel(e.target.value === 'overall' ? 'overall' : parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            >
              <option value="overall">{t('account.overall_option')}</option>
              {(campaignsQ.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.display_name}</option>
              ))}
            </select>
          </div>

          {campaignSel === 'overall' && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              {PERIODS.map((pp) => (
                <button
                  key={pp.value}
                  onClick={() => setPeriod(pp.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-md transition-colors',
                    period === pp.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {t(`periods.${pp.key}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Report */}
      {!accountId ? (
        <Alert variant="info">{t('admin.select_client')}</Alert>
      ) : campaignSel !== 'overall' ? (
        <TrafficQualityPage
          campaignId={campaignSel}
          api={adminTrafficQualityService}
          readOnly
          queryKeyPrefix="admin-tq"
        />
      ) : overallQ.isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : overallQ.isError ? (
        <Alert variant="error">{t('errors.load_failed')}</Alert>
      ) : overallQ.data ? (
        <OverallView data={overallQ.data} />
      ) : null}
    </div>
  );
}

function OverallView({ data }: { data: AccountTrafficQuality }) {
  const { t } = useTranslation('trafficQuality');
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-500">{t('cards.account_quality_score')}</div>
          <div className={cn('text-4xl font-bold mt-1', scoreColor(s.quality_score))}>
            {s.quality_score}<span className="text-lg text-gray-400">/100</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.campaigns_count', { count: s.campaigns_count })}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.bot_clicks')}</div>
          <div className="text-4xl font-bold mt-1 text-red-600">{s.bot.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.bot_of_targetable', { percent: s.bot_percentage })}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.open_alerts')}</div>
          <div className="text-4xl font-bold mt-1 text-orange-600">{s.open_alerts}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.open_alerts_hint')}</div>
        </Card>
      </div>

      <Card header={<h2 className="font-semibold text-gray-900">{t('account.breakdown_title')}</h2>}>
        {data.campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4">{t('account.table.campaign')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.score')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.clicks')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.bots')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.bot_pct')}</th>
                  <th className="py-2 text-right">{t('account.table.alerts')}</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-gray-50">
                    <td className="py-2 pr-4 text-gray-800">{c.name || `#${c.campaign_id}`}</td>
                    <td className={cn('py-2 pr-4 text-right font-medium', scoreColor(c.quality_score))}>{c.quality_score}</td>
                    <td className="py-2 pr-4 text-right">{c.total.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-red-600">{c.bot.toLocaleString()}</td>
                    <td className={cn('py-2 pr-4 text-right', ratioColor(c.bot_percentage))}>{c.bot_percentage}%</td>
                    <td className="py-2 text-right">{c.open_alerts > 0 ? <span className="text-orange-600 font-medium">{c.open_alerts}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-6 text-center">{t('account.empty')}</p>
        )}
      </Card>
    </div>
  );
}
